import type { ReferralGraph, ReferralGraphEdge, ReferralGraphNode } from '@/types/user-statistics'

export const DEFAULT_REFERRAL_SANKEY_LEVELS = 3

const NODE_WIDTH = 138
const COLUMN_GAP = 210
const NODE_GAP = 16
const OUTER_GAP = 24
const LINK_WIDTH = 4
const MIN_NODE_HEIGHT = 50

export interface VisibleReferralSankeyNode extends ReferralGraphNode {
  depth: number
  hiddenChildCount: number
  order: number
}

export interface VisibleReferralSankey {
  nodes: VisibleReferralSankeyNode[]
  edges: ReferralGraphEdge[]
}

export interface ReferralSankeyLayoutNode extends VisibleReferralSankeyNode {
  x: number
  y: number
  width: number
  height: number
}

export interface ReferralSankeyLayoutLink extends ReferralGraphEdge {
  path: string
  width: number
}

export interface ReferralSankeyLayout {
  width: number
  height: number
  nodes: ReferralSankeyLayoutNode[]
  links: ReferralSankeyLayoutLink[]
}

const compareNodes = (left: ReferralGraphNode, right: ReferralGraphNode) => (
  right.outgoingCount - left.outgoingCount
  || left.label.localeCompare(right.label, 'zh-CN')
  || left.id - right.id
)

export const buildVisibleReferralSankey = (
  graph: ReferralGraph,
  expandedNodeIds: ReadonlySet<number>,
  defaultLevels = DEFAULT_REFERRAL_SANKEY_LEVELS,
): VisibleReferralSankey => {
  const nodesById = new Map(graph.nodes.map(node => [node.id, node]))
  const incomingNodeIds = new Set(graph.edges.map(edge => edge.targetId))
  const childrenById = new Map<number, ReferralGraphEdge[]>()

  for (const edge of graph.edges) {
    const children = childrenById.get(edge.sourceId) || []
    children.push(edge)
    childrenById.set(edge.sourceId, children)
  }
  for (const children of childrenById.values()) {
    children.sort((left, right) => {
      const leftNode = nodesById.get(left.targetId)
      const rightNode = nodesById.get(right.targetId)
      if (leftNode && rightNode) return compareNodes(leftNode, rightNode)
      return left.targetId - right.targetId
    })
  }

  const orderedNodes = [...graph.nodes].sort(compareNodes)
  const roots = orderedNodes.filter(node => !incomingNodeIds.has(node.id))
  const coveredNodeIds = new Set<number>()
  const visibleDepths = new Map<number, number>()
  const visibleOrder = new Map<number, number>()
  let order = 0

  const markCovered = (nodeId: number) => {
    if (coveredNodeIds.has(nodeId)) return
    coveredNodeIds.add(nodeId)
    for (const edge of childrenById.get(nodeId) || []) markCovered(edge.targetId)
  }

  const visit = (nodeId: number, depth: number, ancestors: ReadonlySet<number>) => {
    if (visibleDepths.has(nodeId)) return
    visibleDepths.set(nodeId, depth)
    visibleOrder.set(nodeId, order)
    order += 1

    const mayRevealChildren = depth < Math.max(1, defaultLevels) - 1 || expandedNodeIds.has(nodeId)
    if (!mayRevealChildren) return

    const nextAncestors = new Set(ancestors)
    nextAncestors.add(nodeId)
    for (const edge of childrenById.get(nodeId) || []) {
      if (!nodesById.has(edge.targetId) || nextAncestors.has(edge.targetId)) continue
      visit(edge.targetId, depth + 1, nextAncestors)
    }
  }

  for (const root of roots) {
    markCovered(root.id)
    visit(root.id, 0, new Set())
  }
  for (const node of orderedNodes) {
    if (coveredNodeIds.has(node.id)) continue
    markCovered(node.id)
    visit(node.id, 0, new Set())
  }

  const nodes = orderedNodes
    .filter(node => visibleDepths.has(node.id))
    .map(node => ({
      ...node,
      depth: visibleDepths.get(node.id) || 0,
      hiddenChildCount: (childrenById.get(node.id) || [])
        .filter(edge => !visibleDepths.has(edge.targetId)).length,
      order: visibleOrder.get(node.id) || 0,
    }))
    .sort((left, right) => left.order - right.order)
  const visibleNodeIds = new Set(nodes.map(node => node.id))

  return {
    nodes,
    edges: graph.edges.filter(edge => (
      visibleNodeIds.has(edge.sourceId) && visibleNodeIds.has(edge.targetId)
    )),
  }
}

export const layoutReferralSankey = (sankey: VisibleReferralSankey): ReferralSankeyLayout => {
  if (!sankey.nodes.length) return { width: 720, height: 360, nodes: [], links: [] }

  const incomingCounts = new Map<number, number>()
  const outgoingCounts = new Map<number, number>()
  for (const edge of sankey.edges) {
    incomingCounts.set(edge.targetId, (incomingCounts.get(edge.targetId) || 0) + 1)
    outgoingCounts.set(edge.sourceId, (outgoingCounts.get(edge.sourceId) || 0) + 1)
  }

  const columns = new Map<number, VisibleReferralSankeyNode[]>()
  for (const node of sankey.nodes) {
    const column = columns.get(node.depth) || []
    column.push(node)
    columns.set(node.depth, column)
  }

  const layoutNodes: ReferralSankeyLayoutNode[] = []
  let contentHeight = 0
  for (const [depth, nodes] of columns) {
    nodes.sort((left, right) => (
      right.outgoingCount - left.outgoingCount
      || left.order - right.order
    ))
    let y = OUTER_GAP
    for (const node of nodes) {
      const flowCount = Math.max(
        incomingCounts.get(node.id) || 0,
        outgoingCounts.get(node.id) || 0,
        1,
      )
      const height = Math.max(MIN_NODE_HEIGHT, flowCount * LINK_WIDTH + 18)
      layoutNodes.push({
        ...node,
        x: OUTER_GAP + depth * COLUMN_GAP,
        y,
        width: NODE_WIDTH,
        height,
      })
      y += height + NODE_GAP
    }
    contentHeight = Math.max(contentHeight, y - NODE_GAP + OUTER_GAP)
  }

  const nodesById = new Map(layoutNodes.map(node => [node.id, node]))
  const outgoingOffsets = new Map<number, number>()
  const incomingOffsets = new Map<number, number>()
  const links = sankey.edges.flatMap((edge) => {
    const source = nodesById.get(edge.sourceId)
    const target = nodesById.get(edge.targetId)
    if (!source || !target) return []

    const sourceIndex = outgoingOffsets.get(source.id) || 0
    const targetIndex = incomingOffsets.get(target.id) || 0
    outgoingOffsets.set(source.id, sourceIndex + 1)
    incomingOffsets.set(target.id, targetIndex + 1)

    const sourceCount = outgoingCounts.get(source.id) || 1
    const targetCount = incomingCounts.get(target.id) || 1
    const sourceY = source.y + (source.height - sourceCount * LINK_WIDTH) / 2
      + sourceIndex * LINK_WIDTH + LINK_WIDTH / 2
    const targetY = target.y + (target.height - targetCount * LINK_WIDTH) / 2
      + targetIndex * LINK_WIDTH + LINK_WIDTH / 2
    const sourceX = source.x + source.width
    const targetX = target.x
    const curve = Math.max(36, Math.abs(targetX - sourceX) * 0.45)

    return [{
      ...edge,
      path: `M ${sourceX} ${sourceY} C ${sourceX + curve} ${sourceY}, ${targetX - curve} ${targetY}, ${targetX} ${targetY}`,
      width: LINK_WIDTH,
    }]
  })

  const maxDepth = Math.max(...layoutNodes.map(node => node.depth))
  return {
    width: Math.max(720, OUTER_GAP * 2 + maxDepth * COLUMN_GAP + NODE_WIDTH),
    height: Math.max(360, contentHeight),
    nodes: layoutNodes,
    links,
  }
}
